import React from 'react';
import type {Intent} from '@sail/engine';
import {createView, createViewConfig} from '@sail/engine';
import type {View} from '../view';
import {css} from '../css';
import {view} from '../view';
import {tokens} from '../tokens';
import {absurd} from '../lib/absurd';

export type TableCellWidth = 'auto' | 'maximized' | 'minimized' | number;

export type TableCellAlign = 'center' | 'left' | 'right';

export type TableCellVAlign = 'baseline' | 'bottom' | 'middle' | 'top';

export type TableCellProps = Omit<View.IntrinsicElement<'td'>, 'valign'> & {
  /**
   * Determines the horizontal alignment of the cell content. Defaults to 'left'.
   */
  align?: TableCellAlign;
  /**
   * Specifies the number of columns a cell should span.
   */
  colSpan?: number;
  /**
   * List of Sail intents to apply to the cell content.
   */
  contentUses?: Intent[];
  /**
   * The id.
   */
  id?: string;
  /**
   * min-width for the cell content. Any number will be converted to pixels.
   */
  minWidth?: TableCellWidth;
  /**
   * max-width for the cell content. Any number will be converted to pixels.
   */
  maxWidth?: TableCellWidth;
  /**
   * Specifies the number of rows a cell should span.
   */
  rowSpan?: number;
  /**
   * Vertical alignment for the cell content. Defaults to 'top'.
   */
  vAlign?: TableCellVAlign;
  /**
   * Width for the cell content. Any number will be converted to pixels. Defaults to 'auto'.
   */
  width?: TableCellWidth;
  /**
   * Enable or disable white-space wrapping for the cell content. Defaults to true.
   */
  wrap?: boolean;
  /**
   * Cell content.
   */
  children?: React.ReactNode;
};

// This function takes the valid inputs for width/minWidth/maxWidth and
// returns the actual css representation of the value
export const getWidthCSSValueFromProp = (width: TableCellWidth): string => {
  if (width === 'auto') {
    return 'auto';
  } else if (width === 'maximized') {
    return '100%';
  } else if (width === 'minimized') {
    return '1px';
  } else if (typeof width === 'number') {
    return `${width}px`;
  }
  throw absurd(width);
};

export type BasicTableProps = View.IntrinsicElement<'table'>;

export const BasicTableConfig = createViewConfig({
  name: 'BasicTable',
  props: {} as BasicTableProps,
  flattens: true,
});

export const BasicTable = BasicTableConfig.createView((props) => {
  return (
    <view.table
      {...props}
      css={{
        color: 'text',
        maxWidth: 'fill',
        width: 'fill',
      }}
      uses={[css({borderCollapse: 'collapse', borderSpacing: '0'})]}
    />
  );
});

export type BasicTableHeaderProps = View.IntrinsicElement<'thead'>;

export const BasicTableHeaderConfig = createViewConfig({
  name: 'BasicTableHeader',
  props: {} as BasicTableHeaderProps,
  flattens: true,
});

export const BasicTableHeader = BasicTableHeaderConfig.createView('thead');
export type BasicTableBodyProps = View.IntrinsicElement<'tbody'>;

export const BasicTableBodyConfig = createViewConfig({
  name: 'BasicTableBody',
  props: {} as BasicTableBodyProps,
  flattens: true,
});

export const BasicTableBody = BasicTableBodyConfig.createView('tbody');

export type BasicTableRowProps = View.IntrinsicElement<'tr'>;

export const BasicTableRowConfig = createViewConfig({
  name: 'BasicTableRow',
  props: {} as BasicTableRowProps,
  flattens: true,
});

export const BasicTableRow = BasicTableRowConfig.createView('tr');

export type BasicTableFooterProps = View.IntrinsicElement<'tfoot'>;
export const BasicTableFooterConfig = createViewConfig({
  name: 'BasicTableFooter',
  props: {} as BasicTableFooterProps,
  flattens: true,
});
export const BasicTableFooter = BasicTableFooterConfig.createView('tfoot');

export type CellContentProps = View.IntrinsicElement<'div'>;
export const CellContentConfig = createViewConfig({
  name: 'CellContent',
  props: {} as CellContentProps,
  flattens: true,
});
export const CellContent = CellContentConfig.createView('div', {
  css: {
    minHeight: 'space.250',
    marginY: 'small',
    marginRight: 'large',
    position: 'relative',
    stack: 'x',
    display: 'inline',
    alignX: 'start',
    alignY: 'top',
    font: 'body.small',
    // Nasty hack to cancel out Sail's auto-alignment pseudo elements, which are causing some unwanted whitespace
    ':before': {
      content: 'none',
    },
    ':after': {
      content: 'none',
    },
  },
  uses: [
    css({
      fontFeatureSettings: "'tnum'",
      fontVariant: 'tabular-nums',
    }),
  ],
});

const BasicTableCellInternal = createView(
  ({
    align = 'left',
    children,
    colSpan,
    minWidth,
    maxWidth,
    rowSpan,
    vAlign = 'top',
    width = 'auto',
    wrap = true,
    contentUses,
    heading,
    ...rest
  }: TableCellProps & {heading?: boolean}) => {
    // We need memo for props that change css, but not for themed imported variables
    // since those don't require rerenders
    const styles = React.useMemo(
      () =>
        css({
          boxShadow: `inset 0 -1px 0 0 ${tokens.color.neutral[50]}`,
          ...(maxWidth ? {maxWidth: getWidthCSSValueFromProp(maxWidth)} : {}),
          ...(minWidth ? {minWidth: getWidthCSSValueFromProp(minWidth)} : {}),
          padding: 'space.0',
          textAlign: align,
          whiteSpace: wrap ? 'normal' : 'nowrap',
          width: getWidthCSSValueFromProp(width),
        }),
      [align, minWidth, maxWidth, wrap, width],
    );

    const vAlignStyles = React.useMemo(
      () => css({verticalAlign: vAlign}),
      [vAlign],
    );

    // Grab the rowSpan and colSpan attributes only if they're set
    const spanProps: {colSpan?: number; rowSpan?: number} = {};

    if (colSpan) {
      spanProps.colSpan = colSpan;
    }
    if (rowSpan) {
      spanProps.rowSpan = rowSpan;
    }

    const Element = heading ? view.th : view.td;

    return (
      <Element {...rest} {...spanProps} uses={[styles, vAlignStyles]}>
        <CellContent uses={contentUses}>{children}</CellContent>
      </Element>
    );
  },
);

export const BasicTableCellConfig = createViewConfig({
  name: 'BasicTableCell',
  props: {} as TableCellProps,
  flattens: true,
});

export const BasicTableCell = BasicTableCellConfig.createView((props) => {
  return <BasicTableCellInternal {...props} />;
});

export const BasicTableHeaderCellConfig = createViewConfig({
  name: 'BasicTableHeaderCell',
  props: {} as TableCellProps,
  flattens: true,
});

export const BasicTableHeaderCell = BasicTableHeaderCellConfig.createView(
  (props) => {
    return (
      <BasicTableCellInternal
        heading
        contentUses={[css({font: 'heading.xsmall', margin: 'space.0'})]}
        {...props}
      />
    );
  },
);
